import React, { useRef, useState } from "react";
import {
  PiLockKeyLight,
  PiRocketLaunchLight,
  PiArrowRightLight,
  PiShieldCheckLight,
  PiCrownLight,
  PiPuzzlePieceLight,
  PiEnvelopeSimpleLight,
  PiCheckCircleLight,
  PiPlusCircleLight,
} from "react-icons/pi";
import Button from "../../shared/TailwindComponents/Button";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  requestFeatureFromAdmin,
  addAddon,
  changePlan,
  fetchMyAccess,
  LicensePlan,
} from "../../../store/actions/PlanActions";
import { addonForKey, computeFullUpsell } from "../../shared/Licenses/upsell";
import { moduleForAiFeature, moduleForSection, formatEur } from "../../../config/pricing";

interface LicenseGateModalProps {
  /** Human-readable section/feature label */
  title: string;
  /** Explanation of what's needed - can include HTML */
  description: string;
  /** Name of the plan or addon that unlocks this feature */
  planName?: string;
  /** "base" or "addon" */
  category?: string;
  /** The section or feature key being gated (e.g. 'finance', 'review_ai') */
  section?: string;
  onViewPlans: () => void;
  onClose: () => void;
}

export default function LicenseGateModal({
  title,
  description,
  planName,
  category,
  section,
  onViewPlans,
  onClose,
}: LicenseGateModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAddon = category === "addon";
  const isOrgAdmin = useSelector(
    (state: RootState) => state.user.userInfo?.is_org_admin === true
  );
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);
  const plans = useSelector((state: RootState) => state.license?.plans ?? []);
  const access = useSelector((state: RootState) => state.license?.access);

  /*
    Resolve the add-on from the GATED KEY, not from the display name.

    Matching on `display_name` only worked when the caller happened to pass a
    `planName` that matched a catalogue row exactly, which the AI-feature gates
    do not. `addonForKey` looks the section OR the ai_feature up in the
    catalogue, so a blocked `review_ai` finds the Review add-on the same way a
    blocked `reviews` does.
  */
  const matchingAddon = React.useMemo(
    () => addonForKey(section, addons) ||
      (isAddon && planName
        ? addons.find((a: LicensePlan) => a.display_name === planName) ?? null
        : null),
    [section, addons, isAddon, planName]
  );

  /* The catalogue row for what is locked, so the card can name the real
     module and its list price rather than only the licence row. */
  const catalogueModule = section
    ? moduleForSection(section) || moduleForAiFeature(section)
    : null;

  /*
    THE UPSELL, lifted out of `LicenseOverlay`.

    It used to live only on the page-level lock, so a firm that hit the same
    wall through an action gate or a 403 was sold an add-on that costs them
    more than Full. Same calculation, both surfaces - see `upsell.ts`.
  */
  const upsell = React.useMemo(
    () => computeFullUpsell({ isOrgAdmin, matchingAddon, access, plans, addons }),
    [isOrgAdmin, matchingAddon, access, plans, addons]
  );
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const upgradeInFlightRef = useRef(false);

  const handleUpgradeToFull = async () => {
    if (!upsell || upgradeInFlightRef.current) return;
    upgradeInFlightRef.current = true;
    setUpgradeLoading(true);
    try {
      const result = await dispatch(
        changePlan({ new_license_id: upsell.fullPlan.id, billing_period: "monthly" })
      );
      if (result) {
        await dispatch(fetchMyAccess());
        window.location.reload();
      }
    } finally {
      upgradeInFlightRef.current = false;
      setUpgradeLoading(false);
    }
  };
  const [requestSent, setRequestSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);

  /*
    A REF, not the `sending` state, is what stops the second click.

    `setSending(true)` does not take effect until React re-renders, so two
    clicks in the same tick both read `sending === false` and both fire —
    which is two feature requests landing on the admin's queue for one
    section, from one impatient press. The ref is set synchronously.
  */
  const inFlightRef = useRef(false);

  const handleRequestFromAdmin = async () => {
    if (!section || inFlightRef.current) return;
    inFlightRef.current = true;
    setSending(true);
    const success = await dispatch(requestFeatureFromAdmin(section));
    inFlightRef.current = false;
    setSending(false);
    if (success) {
      setRequestSent(true);
    }
  };

  const addonInFlightRef = useRef(false);

  const handleAddAddon = async () => {
    if (!matchingAddon || addonInFlightRef.current) return;
    addonInFlightRef.current = true;
    const orgId = userInfo?.OrganizationID || userInfo?.organization_id;
    const email = userInfo?.Email || userInfo?.email || "";
    if (!orgId) return;

    setAddonLoading(true);
    try {
      const successUrl = `${window.location.origin}/plans?success=true`;
      const cancelUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const result = await dispatch(
        addAddon({
          addon_license_id: matchingAddon.id,
          billing_period: "monthly",
          seat_count: 1,
          email,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.requires_checkout && result?.url) {
        window.location.assign(result.url);
        return;
      }
      // Addon was added directly (org had Stripe sub) - close modal
      if (result) {
        onClose();
        window.location.reload();
      }
    } finally {
      addonInFlightRef.current = false;
      setAddonLoading(false);
    }
  };

  return (
    <div className="px-6 py-4">
      {/* Icon + heading */}
      <div className="flex flex-col items-center text-center mb-6">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-warning-50 dark:bg-warning-900/30 mb-4">
          <PiLockKeyLight className="w-7 h-7 text-warning-600 dark:text-warning-400" />
        </div>
        <p
          className="text-sm text-gray-600 dark:text-gray-300 max-w-sm"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>

      {/* Recommended plan card */}
      {planName && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-gray-700 shadow-sm">
              {isAddon ? (
                <PiPuzzlePieceLight className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              ) : (
                <PiCrownLight className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {planName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isAddon
                  ? t("Add this to your current plan")
                  : t("Upgrade your base plan")}
              </p>
            </div>
            <PiShieldCheckLight className="w-5 h-5 text-success-600 dark:text-success-400" />
          </div>
        </div>
      )}

      {/* What the module actually is, and what it adds - from the v4.2
          catalogue, so the lock names the same thing the pricing page does. */}
      {catalogueModule && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4 text-start">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t(catalogueModule.name)}
            </p>
            <p className="text-sm text-gray-900 dark:text-white tabular-nums">
              {formatEur(catalogueModule.annual)}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {" "}{t("/seat/mo")}
              </span>
            </p>
          </div>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {t(catalogueModule.description)}
          </p>
        </div>
      )}

      {/* THE UPSELL. Only where it is true, only for someone who can act on
          it - see `computeFullUpsell`. */}
      {upsell && (
        <div
          className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 mb-4
                     bg-indigo-50/60 dark:bg-indigo-900/20 text-start"
          data-testid="license-upsell"
        >
          <div className="flex items-center gap-2 mb-2">
            <PiCrownLight className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t("PONS Full is the cheaper way to get this")}
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t("PONS Full")}</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                €{upsell.fullPrice}/{t("mo")} {t("per seat")}
              </p>
            </div>
            <div className="text-end">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("Starter + {{count}} add-ons", { count: upsell.activeAddonCount + 1 })}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 line-through">
                €{upsell.projectedTotal}/{t("mo")}
              </p>
            </div>
          </div>
          {upsell.savings > 0 && (
            <p className="text-xs font-medium text-success-600 dark:text-success-400 mb-2">
              {t("Save €{{amount}}/mo per seat by switching to Full", {
                amount: upsell.savings.toFixed(0),
              })}
            </p>
          )}
          <Button
            variant="primary"
            size="small"
            onClick={handleUpgradeToFull}
            disabled={upgradeLoading}
            className="w-full"
            data-testid="license-upsell-upgrade"
          >
            <PiCrownLight className="w-4 h-4 me-1.5" />
            {upgradeLoading ? t("Upgrading...") : t("Switch to PONS Full")}
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button variant="neutral" size="small" onClick={onClose} className="flex-1">
          {t("Maybe Later")}
        </Button>
        {isOrgAdmin ? (
          matchingAddon ? (
            /*
              TWO WAYS TO BUY, both named.

              This offered the add-on and hid the plan upgrade behind an arrow
              icon, so the only legible option was "add the €59 module" - even
              where moving to Full costs less and includes it. Both are real
              decisions and both are spelled out: the module, and the package
              that bundles it.
            */
            <div className="flex items-center gap-2 flex-1">
              <Button
                variant="primary"
                size="small"
                onClick={handleAddAddon}
                disabled={addonLoading || upgradeLoading}
                className="flex-1"
                data-testid="gate-add-addon"
              >
                <PiPlusCircleLight className="w-4 h-4 me-1.5" />
                {addonLoading ? t("Loading...") : t("Add Add-on")}
              </Button>
              <Button
                variant="neutral"
                size="small"
                onClick={() => { onClose(); onViewPlans(); }}
                disabled={addonLoading || upgradeLoading}
                className="flex-1"
                data-testid="gate-upgrade-plan"
              >
                <PiCrownLight className="w-4 h-4 me-1.5" />
                {t("Upgrade plan")}
              </Button>
            </div>
          ) : (
            /* No add-on sells this one, so upgrading the package is the only
               way to it. */
            <Button
              variant="primary"
              size="small"
              onClick={() => { onClose(); onViewPlans(); }}
              className="flex-1"
              data-testid="gate-upgrade-plan"
            >
              <PiRocketLaunchLight className="w-4 h-4 me-1.5" />
              {t("Upgrade plan")}
              <PiArrowRightLight className="w-4 h-4 ms-1.5 rtl:-scale-x-100" />
            </Button>
          )
        ) : requestSent ? (
          <Button variant="primary" size="small" disabled className="flex-1">
            <PiCheckCircleLight className="w-4 h-4 me-1.5" />
            {t("Request Sent")}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="small"
            onClick={handleRequestFromAdmin}
            disabled={sending}
            className="flex-1"
          >
            <PiEnvelopeSimpleLight className="w-4 h-4 me-1.5" />
            {sending ? t("Sending...") : t("Request from Admin")}
          </Button>
        )}
      </div>
    </div>
  );
}
